import React, { useEffect, useState } from "react";

import {
  addScreeningQuestion,
  updateScreeningQuestion,
} from "../../../services/masters";
import MultiSelect, {
  MultiSelectOption,
} from "../../../components/Inputs/MultiSelect";
import { InfoQuestionType } from "../../../ts/enums";
import Modal, { ModalBody, ModalFooter } from "../../../components/Modal/Modal";
import ScreeningQuestion from "../../../ts/models/ScreeningQuestion";
import PrimaryButton from "../../../components/Buttons/PrimaryButton";
import classes from "./AddScreeningQuestion.module.css";
import Input from "../../../components/Inputs/Input";
import LinkButton from "../../../components/Buttons/LinkButton";
import InputLabel from "../../../components/Inputs/InputLabel";
import trashIcon from "../../../assets/icons/trash.svg";
import Checkbox from "../../../components/Checkbox/Checkbox";
import Select from "../../../components/Inputs/Select";
import { getClasses } from "../../../utils";

const options = [
  {
    name: "Option List",
    type: InfoQuestionType.OptionList,
    icon: String.fromCodePoint(0xf0ca), // fa-list-ul
  },
  {
    name: "Free Text",
    type: InfoQuestionType.FreeText,
    icon: String.fromCodePoint(0xf031), // fa-font
  },
  {
    name: "Number",
    type: InfoQuestionType.Number,
    icon: String.fromCodePoint(0xf292), // fa-hashtag
  },
  {
    name: "Date",
    type: InfoQuestionType.Date,
    icon: String.fromCodePoint(0xf073), // fa-calendar
  },
];

type Props = {
  onClose: (isUpdated: boolean) => void;
  prevData?: ScreeningQuestion;
  allScreeningQuestions: ScreeningQuestion[];
  isEdit: Boolean;
};

const AddScreeningQuestion: React.FC<Props> = ({
  onClose,
  prevData,
  allScreeningQuestions,
  isEdit,
}) => {
  const [data, setData] = useState<ScreeningQuestion>(
    prevData ? prevData : new ScreeningQuestion()
  );
  const [option, setOption] = useState({
    text: "",
    showReason: false,
  });
  const [logicQuestion, setLogicQuestion] = useState<ScreeningQuestion>(
    new ScreeningQuestion()
  );

  const [logicQuestions, setLogicQuestions] = useState<ScreeningQuestion[]>([]);

  useEffect(() => {
    if (isEdit && prevData?.id) {
      const questions = allScreeningQuestions.filter(
        (question) => question.id !== prevData.id && question.options.length > 0
      );
      setLogicQuestions(questions);
      setLogicQuestionData(prevData.logicQuestion, questions);
      if (!prevData?.questionType) {
        setData({ ...prevData, questionType: InfoQuestionType.OptionList });
      }
    } else {
      const questions = allScreeningQuestions.filter(
        (question) => question.options?.length > 0
      );
      setLogicQuestions(questions);
    }
  }, [isEdit]);

  const addMasterHandler = async () => {
    const newMaster = new ScreeningQuestion(
      data.code,
      data.question,
      data.questionType,
      data.options,
      data.logicQuestion,
      data.logicQuestionResponse,
      data.multiSelect
    );
    let res;
    try {
      if (isEdit) {
        newMaster.id = prevData!.id;
        res = await updateScreeningQuestion(newMaster);
      } else {
        res = await addScreeningQuestion(newMaster);
      }
      if (res.status === 200) {
        onClose(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const addOptionHandler = () => {
    if (option.text) {
      setData((state) => ({
        ...state,
        options: [...state.options, option],
      }));
      setOption({ text: "", showReason: false });
    }
  };

  const isRequiredDataMissing = () => {
    if (
      !data.code?.trim() ||
      !data.question?.trim() ||
      (data.options.length === 0 &&
        data.questionType === InfoQuestionType.OptionList) ||
      !data.questionType?.trim() ||
      (data.logicQuestion && !data.logicQuestionResponse)
    ) {
      return true;
    }
    return false;
  };

  const setLogicQuestionData = (
    questionId?: string,
    questions?: ScreeningQuestion[]
  ) => {
    const question = questions?.find((ques) => ques.id === questionId);
    if (question) {
      setLogicQuestion(question);
    }
  };

  const addLogicQuestionHandler = (e: any) => {
    setData((state) => ({
      ...state,
      logicQuestion: e.target.value,
      logicQuestionResponse: "",
    }));
    setLogicQuestionData(e.target.value, logicQuestions);
  };

  return (
    <Modal
      title={
        <>{isEdit ? "Edit Screening Question" : "Add Screening Question"}</>
      }
      onClose={() => onClose(false)}
      style={{ width: "35rem" }}>
      <>
        <ModalBody>
          <div className={classes.main}>
            <Input
              name='Code'
              value={data.code}
              onChange={(e) =>
                setData((state) => ({ ...state, code: e.target.value }))
              }
              className={classes.input}
            />
            <Input
              name='Question'
              value={data.question}
              onChange={(e) =>
                setData((state) => ({
                  ...state,
                  question: e.target.value,
                }))
              }
              className={classes.input}
            />
            <div className={classes.addOptionContainer}>
              <div className={classes.optionInputContainer}>
                <Select
                  label={"Question Type"}
                  htmlFor='type'
                  options={options}
                  value={data.questionType}
                  defaultOption='Select Question Type'
                  nameExtractor={"name"}
                  valueExtractor={"type"}
                  onChange={(e) => {
                    setData((prevState) => {
                      let newState = {
                        ...prevState,
                        questionType: e.target.value,
                      };
                      if (e.target.value !== InfoQuestionType.OptionList) {
                        newState.options = [];
                        newState.multiSelect = false;
                        setOption({ text: "", showReason: false });
                      }
                      return newState;
                    });
                  }}
                  className={classes.questionTypeSelect}
                />
                <div className={classes.addOption}>
                  <Input
                    name='Add Option'
                    value={option.text}
                    onChange={(e) => {
                      setOption((state) => ({
                        ...state,
                        text: e.target.value,
                      }));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        addOptionHandler();
                      }
                    }}
                    disabled={
                      data?.questionType !== InfoQuestionType.OptionList
                    }
                  />
                  <Checkbox
                    className={getClasses(
                      classes.reasonCheckBox,
                      data?.questionType !== InfoQuestionType.OptionList
                        ? classes.disabled
                        : ""
                    )}
                    checked={option.showReason}
                    id={"reason-box"}
                    label={"Show Reason"}
                    onChange={(checked) => {
                      setOption((prevState) => ({
                        ...prevState,
                        showReason: checked,
                      }));
                    }}
                  />
                  <LinkButton
                    onClick={addOptionHandler}
                    label='Add'
                    className={classes.addOptionBtn}
                    disabled={
                      data?.questionType !== InfoQuestionType.OptionList
                    }
                  />
                </div>
              </div>
            </div>
            <div className={classes.optionsContainer}>
              <div className={classes.optionList}>
                <InputLabel label='Options List' />
                {data?.questionType === InfoQuestionType.OptionList && (
                  <Checkbox
                    checked={data.multiSelect}
                    label='Multiple Select'
                    id={"multiple-select"}
                    onChange={() =>
                      setData((prevState) => {
                        const newState = { ...prevState };
                        newState.multiSelect = !newState.multiSelect;
                        return newState;
                      })
                    }
                    className={classes.checkbox}
                  />
                )}
              </div>
              <div className={classes.options}>
                {data.options.map((option, index) => (
                  <div key={index} className={classes.option}>
                    <span>
                      {`${index + 1}. `}
                      {`${option.text} ${
                        option.showReason ? "(with reason)" : ""
                      }`}
                    </span>
                    <img
                      src={trashIcon}
                      alt='delete'
                      onClick={() => {
                        setData((state) => ({
                          ...state,
                          options: state.options.filter(
                            (_, idx) => idx !== index
                          ),
                        }));
                      }}
                      className={classes.trashIcon}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className={classes.logicQuestions}>
              <InputLabel label='Logic' />
              <Select
                label={"Show Question when response to"}
                htmlFor='question'
                options={logicQuestions}
                value={data.logicQuestion ?? ""}
                defaultOption='Select Question'
                nameExtractor={"question"}
                valueExtractor={"id"}
                onChange={addLogicQuestionHandler}
                className={classes.question}
                selectClass={classes.logicQuestionSelect}
              />
              <div className={classes.response}>
                {!logicQuestion?.multiSelect &&
                  logicQuestion.options?.length > 0 && (
                    <Select
                      label={"is"}
                      htmlFor='type'
                      options={logicQuestion?.options?.map((option) => ({
                        name: option.text,
                      }))}
                      value={data.logicQuestionResponse ?? ""}
                      defaultOption='Select Response'
                      nameExtractor={"name"}
                      valueExtractor={"name"}
                      onChange={(e) =>
                        setData((state) => ({
                          ...state,
                          logicQuestionResponse: e.target.value,
                        }))
                      }
                    />
                  )}
                {logicQuestion?.multiSelect &&
                  logicQuestion.options?.length > 0 && (
                    <MultiSelect
                      label='includes'
                      value={
                        data.logicQuestionResponse
                          ? data.logicQuestionResponse
                              ?.split(",")
                              ?.map((res) => ({ label: res, value: res }))
                          : []
                      }
                      options={logicQuestion?.options?.map((option) => ({
                        label: option.text,
                        value: option.text,
                      }))}
                      onChange={(selectedOptions) => {
                        const selectedValues = selectedOptions
                          .map(
                            (selectedOption: MultiSelectOption) =>
                              selectedOption.value
                          )
                          .join(",");
                        setData((prevState) => ({
                          ...prevState,
                          logicQuestionResponse: selectedValues,
                        }));
                      }}
                    />
                  )}
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className={classes.actions}>
            <PrimaryButton
              className={classes.actionBtn}
              name={isEdit ? "Update" : "Add"}
              onClick={addMasterHandler}
              style={{ marginRight: "1rem" }}
              disabled={isRequiredDataMissing()}
            />
            <PrimaryButton
              className={classes.actionBtn}
              name='Cancel'
              onClick={() => onClose(false)}
              transparent
            />
          </div>
        </ModalFooter>
      </>
    </Modal>
  );
};

export default AddScreeningQuestion;
